import React, { useState, useEffect, useMemo } from 'react';
import { Search, X, AlertCircle, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Métadonnées statiques des allergènes (icônes, couleurs, descriptions) ─────
// Ces données ne changent jamais — seules les listes salades/pates viennent de l'API
const ALLERGEN_META = [
  { id: 'gluten',       name: 'Gluten',         icon: '🌾', description: 'Blé, seigle, orge, avoine',          color: '#c8a056' },
  { id: 'crustaces',    name: 'Crustacés',       icon: '🦐', description: 'Crevettes et dérivés',              color: '#e07050' },
  { id: 'oeufs',        name: 'Œufs',            icon: '🥚', description: "Produits à base d'œufs",            color: '#EEB52D' },
  { id: 'poissons',     name: 'Poissons',        icon: '🐟', description: 'Tous types de poissons',            color: '#4a8ec2' },
  { id: 'arachides',    name: 'Arachides',       icon: '🥜', description: 'Cacahuètes et dérivés',             color: '#a07848' },
  { id: 'soja',         name: 'Soja',            icon: '🌱', description: 'Soja, sauce soja, tofu',            color: '#6aab80' },
  { id: 'lait',         name: 'Lait',            icon: '🥛', description: 'Lait, fromages, beurre, crème',     color: '#5a9ad4' },
  { id: 'fruitsacoque', name: 'Fruits à coque',  icon: '🌰', description: 'Noix, noisettes, amandes, cajou',   color: '#8B6040' },
  { id: 'celeri',       name: 'Céleri',          icon: '🌿', description: 'Céleri branche, céleri-rave',       color: '#4a8a60' },
  { id: 'moutarde',     name: 'Moutarde',        icon: '🟡', description: 'Graines, feuilles, dérivés',       color: '#c8a020' },
  { id: 'sesame',       name: 'Sésame',          icon: '⚫', description: "Graines et huile de sésame",       color: '#5a5a5a' },
  { id: 'sulfites',     name: 'Sulfites',        icon: '🍷', description: 'SO₂ — vins, vinaigrettes',         color: '#8a4a7a' },
  { id: 'mollusques',   name: 'Mollusques',      icon: '🐚', description: 'Moules, palourdes, calamar',       color: '#5a8090' },
  { id: 'lupin',        name: 'Lupin',           icon: '🟣', description: 'Farine et graines de lupin',      color: '#7060b0' },
];

// ── Données statiques complètes (fallback si DB vide) ─────────────────────────
const STATIC_ALLERGENS = [
  { id: 'gluten',       salades: ['Croûtons', 'Perles de blé & ciboulette', 'Taboulé', 'Pâtes - Mini Penne', 'Oignons frits'], pates: ['Fusilli', 'Campanelle', 'Mafaldine', 'Radiatori'] },
  { id: 'crustaces',   salades: ['Crevettes'], pates: [] },
  { id: 'oeufs',       salades: ['Œuf dur', 'Sauce Caesar'], pates: ['Fusilli', 'Campanelle', 'Mafaldine', 'Radiatori', 'Carbo Grainette'] },
  { id: 'poissons',    salades: ['Saumon', 'Thon', 'Sauce Nordique'], pates: ['Crème de saumon & ricotta'] },
  { id: 'arachides',   salades: [], pates: [], note: 'Aucun produit identifié. Signalez votre allergie à notre équipe.' },
  { id: 'soja',        salades: ['Edamames', 'Poulet soja/sésame'], pates: [] },
  { id: 'lait',        salades: ['Feta', 'Mozzarella', 'Burratina 50g', 'Grana padano', 'Emmental', 'Mimolette', "Fourme d'Ambert", 'Chèvre', 'Sauce Caesar'], pates: ['Carbo Grainette', 'Crème de cèpes & poulet', 'Crème de saumon & ricotta', 'Crème de truffe', 'Parmesan râpé', 'Emmental râpé', 'Ricotta', 'Mozzarella di Bufala'] },
  { id: 'fruitsacoque', salades: ['Noix', 'Mélange de graines', 'Pesto (sauce)'], pates: ['Pistaches torréfiées', 'Pignons torréfiés', 'Noisettes torréfiées', 'Noix', 'Pesto rosso'] },
  { id: 'celeri',      salades: [], pates: [], note: 'Présence possible dans certaines préparations. Contactez-nous pour confirmation.' },
  { id: 'moutarde',    salades: ['Sauce Ancienne', 'Sauce Miel Moutarde'], pates: [] },
  { id: 'sesame',      salades: ['Graines de sésame', 'Sauce Sésame', 'Poulet soja/sésame'], pates: [] },
  { id: 'sulfites',    salades: ['Raisins secs'], pates: [], note: 'Présence possible dans certaines vinaigrettes. Contactez-nous.' },
  { id: 'mollusques',  salades: [], pates: [], note: 'Aucun produit identifié. Signalez votre allergie à notre équipe.' },
  { id: 'lupin',       salades: [], pates: [], note: 'Aucun produit identifié. Signalez votre allergie à notre équipe.' },
];

// Fusion métadonnées + données statiques en un seul tableau complet
const buildFullAllergens = (staticData) =>
  ALLERGEN_META.map(meta => {
    const data = staticData.find(d => d.id === meta.id) || { salades: [], pates: [] };
    return { ...meta, salades: data.salades || [], pates: data.pates || [], note: data.note };
  });

// ── Transformation données API → format allergènes ────────────────────────────
function buildAllergensFromApi(apiItems) {
  if (!apiItems || apiItems.length === 0) return null;

  // Construire map : allergenId → { salades: [], pates: [] }
  const map = {};
  ALLERGEN_META.forEach(a => { map[a.id] = { salades: [], pates: [] }; });

  apiItems.forEach(item => {
    (item.allergens || []).forEach(aId => {
      if (!map[aId]) return;
      const list = item.concept === 'pates' ? map[aId].pates : map[aId].salades;
      if (!list.includes(item.product)) list.push(item.product);
    });
  });

  return ALLERGEN_META.map(meta => ({
    ...meta,
    salades: map[meta.id]?.salades || [],
    pates:   map[meta.id]?.pates   || [],
    // Conserver la note statique si aucun produit n'est trouvé dans la DB
    note: (map[meta.id]?.salades.length === 0 && map[meta.id]?.pates.length === 0)
      ? (STATIC_ALLERGENS.find(a => a.id === meta.id)?.note)
      : undefined,
  }));
}

export default function Allergenes() {
  const [allergens, setAllergens] = useState(() => buildFullAllergens(STATIC_ALLERGENS));
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetch(`${API}/api/allergens`)
      .then(r => r.ok ? r.json() : [])
      .then(items => {
        const built = buildAllergensFromApi(items);
        if (built) setAllergens(built);
      })
      .catch(() => {}); // fallback silencieux sur les données statiques
  }, []);

  const allergen = useMemo(
    () => allergens.find((a) => a.id === selected),
    [allergens, selected]
  );

  const filteredSalades = useMemo(() => {
    if (!allergen) return [];
    if (!search.trim()) return allergen.salades;
    return allergen.salades.filter((p) =>
      p.toLowerCase().includes(search.toLowerCase())
    );
  }, [allergen, search]);

  const filteredPates = useMemo(() => {
    if (!allergen) return [];
    if (!search.trim()) return allergen.pates;
    return allergen.pates.filter((p) =>
      p.toLowerCase().includes(search.toLowerCase())
    );
  }, [allergen, search]);

  const hasProducts = allergen && (allergen.salades.length > 0 || allergen.pates.length > 0);

  return (
    <div className="bg-[#F5F5F5] min-h-screen pt-16" data-testid="allergenes-page">

      {/* Header */}
      <div className="py-16 px-4 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: '#80a890' }}>
            Transparence &amp; santé
          </p>
          <h1
            className="text-5xl sm:text-6xl font-black mb-4"
            style={{ fontFamily: 'Barlow, sans-serif', color: '#2A3630' }}
          >
            Allergènes
          </h1>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            Sélectionnez votre allergène pour voir tous les produits Grainette concernés, par concept.
          </p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="max-w-5xl mx-auto px-4 pt-8">
        <div
          className="flex items-start gap-3 px-5 py-4 rounded-2xl border text-sm"
          style={{ backgroundColor: '#fffbf0', borderColor: '#f0d870', color: '#7a6000' }}
          data-testid="allergen-disclaimer"
        >
          <AlertCircle size={18} className="mt-0.5 shrink-0" style={{ color: '#c89820' }} />
          <p>
            <strong>Information indicative.</strong> En cas d'allergie sévère, signalez-le systématiquement à notre équipe au moment de la commande.
            Contactez-nous : <a href="mailto:contact@grainette.fr" className="font-semibold underline">contact@grainette.fr</a>
          </p>
        </div>
      </div>

      {/* Grille allergènes */}
      <section className="max-w-5xl mx-auto px-4 py-8" data-testid="allergen-grid-section">
        <p className="text-sm font-semibold text-gray-400 uppercase tracking-[0.1em] mb-4">
          Cliquez sur votre allergène
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {allergens.map((a) => (
            <button
              key={a.id}
              data-testid={`allergen-btn-${a.id}`}
              onClick={() => {
                setSelected(selected === a.id ? null : a.id);
                setSearch('');
              }}
              className="flex flex-col items-center gap-1.5 px-3 py-4 rounded-2xl border-2 text-center transition-all duration-200 select-none"
              style={{
                backgroundColor: selected === a.id ? a.color : '#fff',
                borderColor: selected === a.id ? a.color : '#e5e5e5',
                color: selected === a.id ? '#fff' : '#2A3630',
                transform: selected === a.id ? 'scale(1.04)' : 'scale(1)',
                boxShadow: selected === a.id
                  ? `0 6px 20px ${a.color}40`
                  : '0 1px 3px rgba(0,0,0,0.06)',
              }}
            >
              <span className="text-2xl leading-none">{a.icon}</span>
              <span className="text-xs font-bold leading-tight" style={{ fontFamily: 'Barlow, sans-serif' }}>
                {a.name}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* Panneau de détail */}
      {selected && allergen && (
        <section
          className="max-w-5xl mx-auto px-4 pb-16"
          data-testid="allergen-detail-panel"
          style={{ animation: 'fadeIn 0.2s ease' }}
        >
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            {/* En-tête du panneau */}
            <div
              className="px-6 py-5 flex items-center justify-between"
              style={{ backgroundColor: allergen.color, color: '#fff' }}
            >
              <div className="flex items-center gap-3">
                <span className="text-3xl">{allergen.icon}</span>
                <div>
                  <h2 className="text-xl font-black" style={{ fontFamily: 'Barlow, sans-serif' }}>
                    {allergen.name}
                  </h2>
                  <p className="text-sm opacity-80">{allergen.description}</p>
                </div>
              </div>
              <button
                onClick={() => { setSelected(null); setSearch(''); }}
                className="p-2 rounded-full hover:bg-white/20 transition-colors"
                data-testid="close-allergen-panel"
              >
                <X size={18} />
              </button>
            </div>

            {/* Contenu du panneau */}
            <div className="p-6">
              {/* Note spéciale */}
              {allergen.note && (
                <div className="mb-4 px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-sm text-gray-600 flex items-center gap-2">
                  <AlertCircle size={15} className="shrink-0 text-gray-400" />
                  {allergen.note}
                </div>
              )}

              {/* Barre de recherche */}
              {hasProducts && (
                <div className="relative mb-6">
                  <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Filtrer les produits..."
                    data-testid="allergen-search-input"
                    className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-gray-300 focus:bg-white transition-colors"
                  />
                  {search && (
                    <button
                      onClick={() => setSearch('')}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              )}

              {hasProducts ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Salades */}
                  <div data-testid="allergen-salades-section">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#80a890' }} />
                      <h3 className="font-black text-sm uppercase tracking-wider text-gray-700" style={{ fontFamily: 'Barlow, sans-serif' }}>
                        Bar à Salade
                      </h3>
                    </div>
                    {filteredSalades.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {filteredSalades.map((p) => (
                          <span
                            key={p}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border"
                            style={{ backgroundColor: 'rgba(128,168,144,0.1)', borderColor: 'rgba(128,168,144,0.3)', color: '#2A3630' }}
                          >
                            <ChevronRight size={10} style={{ color: '#80a890' }} />
                            {p}
                          </span>
                        ))}
                      </div>
                    ) : allergen.salades.length === 0 ? (
                      <p className="text-sm text-gray-400 italic">Aucun produit concerné</p>
                    ) : (
                      <p className="text-sm text-gray-400 italic">Aucun résultat pour "{search}"</p>
                    )}
                  </div>

                  {/* Pâtes */}
                  <div data-testid="allergen-pates-section">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#EEB52D' }} />
                      <h3 className="font-black text-sm uppercase tracking-wider text-gray-700" style={{ fontFamily: 'Barlow, sans-serif' }}>
                        Fabrique à Pâtes
                      </h3>
                    </div>
                    {filteredPates.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {filteredPates.map((p) => (
                          <span
                            key={p}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border"
                            style={{ backgroundColor: 'rgba(238,181,45,0.1)', borderColor: 'rgba(238,181,45,0.3)', color: '#2A3630' }}
                          >
                            <ChevronRight size={10} style={{ color: '#EEB52D' }} />
                            {p}
                          </span>
                        ))}
                      </div>
                    ) : allergen.pates.length === 0 ? (
                      <p className="text-sm text-gray-400 italic">Aucun produit concerné</p>
                    ) : (
                      <p className="text-sm text-gray-400 italic">Aucun résultat pour "{search}"</p>
                    )}
                  </div>
                </div>
              ) : !allergen.note ? (
                <p className="text-sm text-gray-400 italic text-center py-4">Aucun produit identifié pour cet allergène.</p>
              ) : null}
            </div>
          </div>
        </section>
      )}

      {/* CTA contact si rien sélectionné */}
      {!selected && (
        <div className="max-w-5xl mx-auto px-4 pb-16 text-center" data-testid="allergen-contact-cta">
          <p className="text-sm text-gray-400">
            Une question sur nos ingrédients ?{' '}
            <a href="/contact" className="font-semibold underline" style={{ color: '#80a890' }}>
              Contactez-nous
            </a>
          </p>
        </div>
      )}
    </div>
  );
}
